import { Card, CardContent } from '@1trading/react/components/ui/card';
import { cn } from '@1trading/react/lib/utils';
import { ArrowDown } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const SCROLL_END_THRESHOLD = 0.95;

const AgreementHeading = ({ children }: React.PropsWithChildren) => <h2 className="-xs:text-sm text-base font-medium">{children}</h2>;
AgreementHeading.displayName = 'AgreementHeading';

const AgreementParagraph = ({ children, className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('-xs:text-xs text-sm', className)} {...props}>
    {children}
  </p>
);
AgreementParagraph.displayName = 'AgreementParagraph';

const AgreementScrollIndicator = () => {
  const { t } = useTranslation();

  return (
    <div className="absolute left-0 bottom-6 w-full flex justify-center pointer-events-none">
      <span className="flex items-center gap-2 py-2 px-4 rounded-lg bg-primary/80 text-xs text-primary-foreground" role="alert" aria-live="polite">
        <ArrowDown className="shrink-0 size-4 animate-bounce" />
        {t('fundamentals.scrollToReadAllAndContinue')}
      </span>
    </div>
  );
};
AgreementScrollIndicator.displayName = 'AgreementScrollIndicator';

const Agreement = ({ children, className, onScrollEnd }: React.ComponentProps<typeof Card> & { onScrollEnd: VoidFunction }) => {
  const [hasScrollIndicator, setHasScrollIndicator] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const { scrollHeight, clientHeight, scrollTop } = element;

    setHasScrolled(true);

    if ((scrollHeight - scrollTop) * SCROLL_END_THRESHOLD <= clientHeight) onScrollEnd();
  };

  return (
    <Card className={cn('-md:flex -md:max-h-full -md:overflow-hidden bg-background shadow-none relative', className)}>
      <CardContent className="-md:flex-1 -md:flex -md:overflow-hidden p-0">
        <article
          ref={(element) => {
            if (!element) return;

            if (element.scrollHeight > element.clientHeight) setHasScrollIndicator(true);
            else onScrollEnd();
          }}
          className="-md:flex-1 -md:max-h-full max-h-[30rem] -2xs:p-4 p-6 overflow-y-scroll scrollbar scrollbar-w-2 scrollbar-thumb-rounded-lg scrollbar-thumb-border scrollbar-track-rounded-lg hover:scrollbar-thumb-muted-foreground/50 space-y-4"
          onScroll={handleScroll}
        >
          {children}
          {hasScrollIndicator && !hasScrolled ? <AgreementScrollIndicator /> : null}
        </article>
      </CardContent>
    </Card>
  );
};
Agreement.displayName = 'Agreement';

export { Agreement, AgreementHeading, AgreementParagraph };
